import React, { useCallback, useState } from 'react';
import { useSnackbar } from 'notistack';
import { Box, Button, FormControl, FormControlLabel, FormLabel, Radio, RadioGroup, Typography } from '@mui/material';
import { useMeetingHubContext } from 'components/meetings/MeetingPage/MeetingHubContext';
import { useMeetingStaffContext } from 'components/meetings/MeetingPage/MeetingStaffView/MeetingStaffContext';
import MeetingService from 'services/MeetingService';
import GridContainer from 'components/shared/GridContainer';
import GridItem from 'components/shared/GridItem';
import PatentedSection from './PatentedSection';
import CopyrightedSection from './CopyrightedSection/CopyrightedSection';
import TrademarkSection from './TrademarkSection/TrademarkSection';
import IPCheckCompletionSection from './IPCheckCompletionSection';
import { ActivityType, IPCheckType, QuestionResponse } from 'store/enums/BallotReviewTypes';
import { VotingSession } from 'store/types/Meeting';
import { VotingRuleType } from 'store/enums/VoteTypes';
import { ipCheckTypeLabel, ipQuestion, meetingSessionToActivity } from 'util/meeting';
import { defaultSnackbarErrorProps } from 'util/layout';

import commonStyles from 'styles/common.module.scss';

const motionId = `ip-check-voting-motion`;

const IPCheckSection: React.FunctionComponent = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { meetingId, attendees, meetingSession } = useMeetingHubContext();
  const { logActivity } = useMeetingStaffContext();
  const [type, setType] = useState<IPCheckType | undefined>(meetingSession?.currentIpCheckSession?.type);
  const [patentConflict, setPatentConflict] = useState<QuestionResponse | undefined>(
    meetingSession?.currentIpCheckSession?.patentConflict
  );

  const setCheckIPType = useCallback(
    async (value: IPCheckType) => {
      const nextPatentConflict = value === IPCheckType.Affirmative ? patentConflict : undefined;

      setType(value);
      setPatentConflict(nextPatentConflict);
      MeetingService.saveMeetingSession({
        ...meetingSession,
        meetingId,
        currentIpCheckSession: {
          ...meetingSession?.currentIpCheckSession,
          type: value,
          patentConflict: nextPatentConflict,
        },
      });
    },
    [meetingId, meetingSession, patentConflict]
  );

  const setKnownIpPatentConflict = useCallback(
    async (value: QuestionResponse) => {
      setPatentConflict(value);
      MeetingService.saveMeetingSession({
        ...meetingSession,
        meetingId,
        currentIpCheckSession: {
          ...meetingSession?.currentIpCheckSession,
          type: type ?? IPCheckType.Affirmative,
          patentConflict: value,
        },
      });
    },
    [meetingId, meetingSession, type]
  );

  const handleVote = useCallback(async () => {
    try {
      const voters = attendees?.filter((a) => {
        return a.affiliate?.toLowerCase() !== 'semi';
      });

      const votingSession: VotingSession = {
        motionId,
        motionStatement: 'IP Check (The presiding chair asked those participating in the meeting)',
        rule: VotingRuleType.SimpleMajority,
        voters: voters?.map((a) => a.id) ?? [],
      };

      await MeetingService.clearVotes(meetingId, motionId);

      await MeetingService.saveMeetingSession(
        meetingSession?.currentDocument
          ? {
              ...meetingSession,
              meetingId,
              currentDocument: {
                ...meetingSession.currentDocument,
                lineItemNumber: undefined,
                lineItemTitle: undefined,
              },
              currentIpCheckVoting: votingSession,
              currentMotion: undefined,
              currentVotingSession: undefined,
            }
          : {
              ...meetingSession,
              meetingId,
              currentIpCheckVoting: votingSession,
              currentMotion: undefined,
              currentVotingSession: undefined,
            }
      );

      enqueueSnackbar(`IP check session started`, { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(`Failed to start IP check session`, defaultSnackbarErrorProps);
      console.error('Error starting IP check session:', error);
    }
  }, [attendees, enqueueSnackbar, meetingId, meetingSession]);

  const handleClear = useCallback(async () => {
    try {
      setType(undefined);
      setPatentConflict(undefined);
      await MeetingService.clearVotes(meetingId, motionId);
      await MeetingService.saveMeetingSession({
        ...meetingSession,
        meetingId,
        currentIpCheckVoting: undefined,
        currentIpCheckSession: undefined,
        currentTechnologyConflict: undefined,
      });

      enqueueSnackbar(`Cleared IP check session`, { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(`Failed to clear IP check session`, defaultSnackbarErrorProps);
      console.error('Error clearing IP check session:', error);
    }
  }, [enqueueSnackbar, meetingId, meetingSession]);

  const handleSave = useCallback(async () => {
    if (type) {
      logActivity(
        {
          ...meetingSessionToActivity(meetingSession),
          type: ActivityType.IpCheck,
          ipCheckSession: {
            type,
            patentConflict,
          },
        },
        'Saved IP Check result'
      );
    }
  }, [logActivity, meetingSession, patentConflict, type]);

  return (
    <GridContainer>
      <GridItem>
        <Typography>{ipQuestion}</Typography>
      </GridItem>
      <GridItem className={commonStyles.buttonGroup}>
        <Button variant="contained" color="secondary" onClick={handleVote}>
          Ask IP Check
        </Button>
        <Button variant="contained" color="error" onClick={handleClear}>
          Clear IP Check
        </Button>
      </GridItem>
      <GridItem>
        <FormControl>
          <FormLabel>Result</FormLabel>
          <RadioGroup
            name="ip-check-type-radio-group"
            value={type ?? ''}
            onChange={(event) => setCheckIPType(event.target.value as IPCheckType)}
          >
            <FormControlLabel
              value={IPCheckType.NotAffirmative}
              control={<Radio />}
              label={
                <Box
                  sx={{ typography: 'body1' }}
                  dangerouslySetInnerHTML={{ __html: ipCheckTypeLabel[IPCheckType.NotAffirmative] }}
                />
              }
            />
            <FormControlLabel
              value={IPCheckType.Affirmative}
              control={<Radio />}
              label={
                <Box
                  sx={{ typography: 'body1' }}
                  dangerouslySetInnerHTML={{ __html: ipCheckTypeLabel[IPCheckType.Affirmative] }}
                />
              }
            />
          </RadioGroup>
        </FormControl>
      </GridItem>
      <GridItem className={commonStyles.buttonGroup}>
        <Button variant="contained" color="secondary" onClick={handleSave}>
          Save
        </Button>
        <Button variant="contained" color="error" onClick={handleClear}>
          Clear
        </Button>
      </GridItem>
      {type === IPCheckType.Affirmative && (
        <GridItem>
          <FormControl>
            <FormLabel>Is any of the known IPs a patented technology?</FormLabel>
            <RadioGroup
              name="known-ip-radio-group"
              value={patentConflict ?? ''}
              onChange={(event) => setKnownIpPatentConflict(event.target.value as QuestionResponse)}
            >
              <FormControlLabel
                value={QuestionResponse.Yes}
                control={<Radio />}
                label={'Yes, at least one of them is a patented technology'}
              />
              <FormControlLabel value={QuestionResponse.No} control={<Radio />} label={'No'} />
            </RadioGroup>
          </FormControl>
        </GridItem>
      )}
      {type === IPCheckType.Affirmative && patentConflict === QuestionResponse.Yes && (
        <GridItem>
          <PatentedSection />
        </GridItem>
      )}
      {type === IPCheckType.Affirmative && patentConflict && (
        <GridItem>
          <CopyrightedSection />
        </GridItem>
      )}
      {type === IPCheckType.Affirmative && (
        <>
          <GridItem>
            <TrademarkSection />
          </GridItem>
          <GridItem>
            <IPCheckCompletionSection />
          </GridItem>
        </>
      )}
    </GridContainer>
  );
};

export default IPCheckSection;
